/* eslint-disable no-console */
import { useMemo } from 'react'
import {
  ApolloClient,
  ApolloError,
  ApolloLink,
  FetchResult,
  HttpLink,
  InMemoryCache,
  Observable,
} from '@apollo/client'
import { onError } from '@apollo/client/link/error'
import { GraphQLError, GraphQLFormattedError } from 'graphql'
import { gql } from '~/__generated__'
import {
  clearTokens,
  getAccessToken,
  getRefreshToken,
  setAccessToken,
  setRefreshToken,
} from './tokens'

const httpLink = new HttpLink({
  uri: '/gql',
})

const roundTripLink = new ApolloLink((operation, forward) => {
  // Called before operation is sent to server
  operation.setContext({ start: new Date().valueOf() })
  return forward(operation).map((data) => {
    // Called after server responds
    const time = new Date().valueOf() - operation.getContext().start
    console.log(`[GQL] ${operation.operationName} ${time}ms`)
    return data
  })
})

export const isUnauthenticated = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  errors: any[] | readonly GraphQLFormattedError[] | undefined
) => {
  return errors?.some((err) => err.extensions?.code === 'UNAUTHENTICATED')
}

const errorLink = onError(
  ({ forward, graphQLErrors, networkError, operation }) => {
    if (isUnauthenticated(graphQLErrors)) {
      if (operation.operationName === 'LoginUser') {
        return
      }
      // ignore 401 error for a refresh request
      if (operation.operationName === 'RefreshToken') {
        clearTokens()
        return
      }

      const observable = new Observable<FetchResult<Record<string, unknown>>>(
        (observer) => {
          // used an annonymous function for using an async function
          ;(async () => {
            try {
              const accessToken = await refreshToken()
              if (!accessToken) {
                throw new GraphQLError('Empty AccessToken')
              }
              // Retry the failed request
              const subscriber = {
                next: observer.next.bind(observer),
                error: observer.error.bind(observer),
                complete: observer.complete.bind(observer),
              }
              forward(operation).subscribe(subscriber)
            } catch (err) {
              clearTokens()
              observer.error(err)
            }
          })()
        }
      )

      return observable
    }
    if (networkError) {
      console.log(`[Network error]: ${networkError}`, networkError)
    }
  }
)

const REFRESH_TOKEN = gql(`
  mutation RefreshToken {
    refreshTokens {
      accessToken
      refreshToken
    }
  }
`)

export const refreshToken = async () => {
  try {
    const refreshResolverResponse = await apolloClient.mutate({
      mutation: REFRESH_TOKEN,
    })
    const { accessToken, refreshToken } =
      refreshResolverResponse.data?.refreshTokens || {}
    setAccessToken(accessToken)
    setRefreshToken(refreshToken)
    return accessToken
  } catch (err) {
    clearTokens()
    throw err
  }
}

const authLink = new ApolloLink((operation, forward) => {
  operation.setContext(({ headers }: { headers: Record<string, string> }) => {
    const token = ['Logout', 'RefreshToken'].includes(operation.operationName)
      ? getRefreshToken()
      : getAccessToken()
    if (token) {
      return {
        headers: {
          ...headers,
          authorization: `Bearer ${token}`,
        },
      }
    }
    return { headers }
  })
  return forward(operation)
})

export const apolloClient = new ApolloClient({
  // uri: '/gql',
  link: ApolloLink.from([roundTripLink, errorLink, authLink, httpLink]),
  cache: new InMemoryCache(),
})

export const clearApolloCache = () => {
  apolloClient.clearStore()
  apolloClient.resetStore()
}

export const useApolloError = (error: ApolloError | undefined) => {
  const parsedErrors = useMemo(() => {
    const form: Record<string, { type: string; message: string }> = {}
    error?.graphQLErrors?.forEach((err) => {
      if (
        err.extensions?.code === 'BAD_REQUEST' &&
        Array.isArray(err.message)
      ) {
        err.message.forEach(({ property, message }) => {
          form[property] = {
            type: 'custom',
            message: message
              .replace(/^.* (should not be empty)/, '$1')
              .replace(/^.* (must contain at least .*)/, '$1'),
          }
        })
      }
    })
    const result: {
      form?: Record<string, { type: string; message: string }>
      message?: string
    } = {}
    if (Object.keys(form).length) {
      result.form = form
    } else if (error?.message) {
      result.message = error?.message
    }
    return Object.keys(result).length ? result : undefined
  }, [error])

  return parsedErrors
}
